import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { destinationDetails } from "../../mocks/destinationDetails";
import DestinationTabs from "./components/DestinationTabs";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const difficultyStyle: Record<string, string> = {
  輕鬆: "bg-green-500 text-white",
  中等: "bg-amber-500 text-white",
  進階: "bg-red-500 text-white",
  地獄: "bg-zinc-900 text-orange-400",
};

const DestinationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);

  const destination = destinationDetails.find(
    (d) => d.id === parseInt(id ?? "0", 10)
  );

  // Sticky bar scroll logic
  useEffect(() => {
    const handleScroll = () => {
      setShowSticky(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!destination) {
      navigate("/", { replace: true });
      return;
    }

    const prevTitle = document.title;
    document.title = `${destination.name}自駕遊 ${destination.duration} | Chillgo · 香港出發 ${destination.price}`;

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") ?? "";
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      `${destination.name}（${destination.subtitle}）${destination.duration}，${destination.price}起。${destination.description.slice(0, 80)}。由香港Chillgo出發，專業嚮導，全程護航。`
    );

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", `${destination.name} | Chillgo · 香港自駕遊`);

    // JSON-LD Schema
    const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "https://example.com";
    const isQuoteOnly = destination.price === "需要報價";
    const priceRaw = isQuoteOnly ? "0" : destination.price.replace(/[^0-9]/g, "");
    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.id = "destination-jsonld";
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TouristTrip",
      name: `${destination.name}自駕遊 ${destination.duration}`,
      description: destination.description,
      url: `${siteUrl}/destination/${destination.id}`,
      image: destination.image,
      touristType: "自駕旅遊",
      offers: isQuoteOnly ? {
        "@type": "Offer",
        description: "請聯絡顧問索取報價",
        availability: "https://schema.org/InStock",
        priceCurrency: "HKD",
      } : {
        "@type": "Offer",
        price: priceRaw,
        priceCurrency: "HKD",
        availability: "https://schema.org/InStock",
        validFrom: "2026-01-01",
      },
      itinerary: {
        "@type": "ItemList",
        name: `${destination.name}行程`,
        description: `由${destination.startCity}至${destination.endCity}，歷時${destination.duration}`,
      },
      provider: {
        "@type": "TravelAgency",
        name: "Chillgo",
        url: siteUrl,
        telephone: "+852-5348-2337",
        address: {
          "@type": "PostalAddress",
          addressLocality: "旺角",
          addressRegion: "九龍",
          addressCountry: "HK",
        },
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首頁", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "目的地", item: `${siteUrl}/#destinations` },
          { "@type": "ListItem", position: 3, name: destination.name, item: `${siteUrl}/destination/${destination.id}` },
        ],
      },
    });
    document.head.appendChild(schemaScript);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
      const existing = document.getElementById("destination-jsonld");
      if (existing) existing.remove();
    };
  }, [destination, navigate]);

  if (!destination) return null;

  const waMsg = encodeURIComponent(`你好，我想查詢 ${destination.name} ${destination.duration} 行程，請問有哪些可用日期？`);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-100">
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
                alt="Chill Go Logo"
                className="h-11 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-sm text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="hidden sm:inline">返回主頁</span>
              </Link>
              <a
                href={`https://wa.me/85253482337?text=${waMsg}`}
                target="_blank"
                rel="nofollow noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                <span>即時查詢</span>
              </a>
              <Link
                to="/custom-itinerary"
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer hidden md:flex items-center gap-1.5"
              >
                <i className="ri-fire-line"></i>
                <span>自訂行程</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[72vh] min-h-[520px] flex items-end">
        <div className="absolute inset-0">
          <img
            src={destination.image}
            alt={`${destination.name}自駕遊 - Chillgo`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/10"></div>
        </div>

        {/* Breadcrumb */}
        <div className="absolute top-20 left-6 lg:left-12 flex items-center gap-2 text-white/60 text-sm">
          <Link to="/" className="hover:text-white transition-colors cursor-pointer">首頁</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="hover:text-white transition-colors">目的地</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white">{destination.name}</span>
        </div>

        <div className="relative w-full px-6 lg:px-12 pb-12">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2.5 mb-5">
              <span className="bg-orange-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                {destination.subtitle}
              </span>
              <span className={`text-sm font-semibold px-3 py-1.5 rounded-full ${difficultyStyle[destination.difficulty] ?? "bg-white/20 text-white"}`}>
                {destination.difficulty}難度
              </span>
              {destination.price !== "需要報價" && (
                <span className="bg-white/15 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full">
                  {destination.duration}
                </span>
              )}
              <span className="bg-white/15 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full">
                最佳季節：{destination.bestSeason}
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
              {destination.name}
            </h1>
            <p className="text-white/75 text-lg max-w-2xl leading-relaxed mb-8">
              {destination.description.slice(0, 130)}…
            </p>

            <div className="flex flex-wrap items-center gap-8">
              <div>
                <div className="text-white/60 text-xs mb-1">
                  {destination.price === "需要報價" ? "費用" : "每人起價"}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`font-bold ${destination.price === "需要報價" ? "text-orange-300 text-2xl" : "text-orange-400 text-4xl"}`}>
                    {destination.price}
                  </span>
                  {destination.price !== "需要報價" && (
                    <span className="text-white/60 text-sm">/人</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-white/60 text-xs mb-1">路線</div>
                <div className="text-white font-medium">
                  {destination.startCity} <span className="text-orange-400">→</span> {destination.endCity}
                </div>
              </div>
              <div className="flex gap-3">
                <a
                  href={`https://wa.me/85253482337?text=${waMsg}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="bg-green-500 hover:bg-green-600 text-white px-7 py-3 rounded-full font-semibold transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  <span>WhatsApp 查詢</span>
                </a>
                <Link
                  to="/custom-itinerary"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-3 rounded-full font-semibold transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <span>自訂此行程</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <main>
        <DestinationTabs destination={destination} />
      </main>

      {/* Bottom CTA */}
      <section className="py-16 bg-zinc-900">
        <div className="w-full px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            準備好出發了嗎？
          </h2>
          <p className="text-stone-400 mb-8 text-base">
            免費諮詢 · 最快 3 天出發 · 無限次修改行程方案
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href={`https://wa.me/85253482337?text=${waMsg}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold text-base transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp 即時預訂</span>
            </a>
            <Link
              to="/custom-itinerary"
              className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white px-8 py-4 rounded-full font-semibold text-base transition-all cursor-pointer whitespace-nowrap"
            >
              全自訂專屬行程
            </Link>
            <Link
              to="/"
              className="text-stone-400 hover:text-white px-8 py-4 rounded-full text-base transition-all cursor-pointer whitespace-nowrap"
            >
              查看更多路線 →
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-800 py-7 border-t border-stone-700">
        <div className="w-full px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/96cdb2bb-ebb4-437f-823a-377697c8f113.png"
              alt="Chill Go Logo"
              className="h-10 w-auto object-contain"
            />
          </Link>
          <p className="text-stone-500 text-sm">
            © {new Date().getFullYear()} Chill Go Limited. 版權所有，不得轉載。
          </p>
          <Link to="/" className="text-stone-400 hover:text-orange-400 text-sm transition-colors cursor-pointer">
            返回主頁
          </Link>
        </div>
      </footer>

      <WhatsAppButton />

      {/* Sticky Bottom CTA Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-500 ${
          showSticky ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-white border-t border-stone-200 shadow-[0_-4px_24px_rgba(0,0,0,0.10)]">
          <div className="w-full px-6 lg:px-12 py-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Left: trip info */}
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={destination?.image}
                    alt={destination?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm truncate">{destination?.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-orange-500 font-semibold text-sm">{destination?.price}</span>
                    {destination?.price !== "需要報價" && (
                      <span className="text-gray-400 text-xs">/人起</span>
                    )}
                    {destination?.price !== "需要報價" && (
                      <>
                        <span className="text-gray-300 text-xs">·</span>
                        <span className="text-gray-500 text-xs">{destination?.duration}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: CTA buttons */}
              <div className="flex items-center gap-2.5 flex-shrink-0">
                <Link
                  to="/custom-itinerary"
                  className="hidden sm:flex items-center gap-1.5 border-2 border-orange-400 text-orange-500 hover:bg-orange-500 hover:text-white px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line text-sm"></i>
                  <span>自訂此行程</span>
                </Link>
                <a
                  href={`https://wa.me/85253482337?text=${encodeURIComponent(`你好，我想查詢「${destination?.name}」${destination?.duration}行程，請問有哪些可用日期及報價？`)}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  <span>WhatsApp 詢問此行程</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationPage;
